import React, { useEffect, useMemo, useRef, useState } from "react";
import { api, API, formatDate, formatTRY, todayISODate } from "@/lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Plus, Pencil, Trash2, Upload, FileText, Download, X, Building2, Calendar, ChevronDown, ChevronUp, CheckCircle } from "lucide-react";
import { toast } from "sonner";
import { TableWrap, Thead, EmptyRow } from "@/components/DataTable";

const STATUSES = ["Beklemede", "Tahsil Edildi"];
const POLICY_TYPES = ["Kasko", "Trafik", "Diğer"];
const STATUS_COLOR = {
  "Beklemede": "bg-amber-50 text-amber-800 border-amber-200",
  "Tahsil Edildi": "bg-emerald-50 text-emerald-800 border-emerald-200",
};
const TABS = [
  { key: "all", label: "Tümü" },
  { key: "pending", label: "Bekleyen Alacaklar" },
  { key: "collected", label: "Tahsil Edilenler" },
];

// Ay seçici için son 12 ayı üret
const buildMonthOptions = () => {
  const arr = [];
  const now = new Date();
  for (let i = 0; i < 12; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    const label = d.toLocaleDateString("tr-TR", { month: "long", year: "numeric" });
    arr.push({ value, label });
  }
  return arr;
};

const emptyForm = {
  customer_id: "",
  customer_name: "",
  plate: "",
  car_model: "",
  policy_number: "",
  insurance_company: "",
  insured_name: "",
  policy_type: "Kasko",
  incident_date: "",
  damage_amount: 0,
  approved_amount: 0,
  collected_amount: 0,
  vat_rate: 0,
  status: "Beklemede",
  notes: "",
  date: todayISODate(),
};

export default function Insurance() {
  const monthOptions = useMemo(() => buildMonthOptions(), []);
  const [selectedMonth, setSelectedMonth] = useState(monthOptions[0].value);
  const [items, setItems] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [summary, setSummary] = useState(null);
  const [byCompany, setByCompany] = useState([]);
  const [companyReportOpen, setCompanyReportOpen] = useState(true);
  const [tab, setTab] = useState("all");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [deleteId, setDeleteId] = useState(null);
  const [detailCase, setDetailCase] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef();

  const load = async () => {
    const [i, c, s] = await Promise.all([
      api.get("/insurance"),
      api.get("/customers"),
      api.get(`/insurance/summary?month=${selectedMonth}`),
    ]);
    setItems(i.data);
    setCustomers(c.data);
    setSummary(s.data);
    if (detailCase) {
      const fresh = i.data.find((x) => x.id === detailCase.id);
      if (fresh) setDetailCase(fresh);
    }
  };
  useEffect(() => { load(); }, [selectedMonth]); // eslint-disable-line

  const openNew = () => { setEditing(null); setForm(emptyForm); setOpen(true); };
  const openEdit = (c) => {
    setEditing(c);
    setForm({
      customer_id: c.customer_id || "",
      customer_name: c.customer_name || "",
      plate: c.plate || "",
      car_model: c.car_model || "",
      policy_number: c.policy_number || "",
      insurance_company: c.insurance_company || "",
      insured_name: c.insured_name || "",
      policy_type: c.policy_type || "Kasko",
      incident_date: c.incident_date ? c.incident_date.slice(0, 10) : "",
      damage_amount: c.damage_amount || 0,
      approved_amount: c.approved_amount || 0,
      collected_amount: c.collected_amount || 0,
      vat_rate: c.vat_rate || 0,
      status: c.status || "Beklemede",
      notes: c.notes || "",
      date: (c.date || todayISODate()).slice(0, 10),
    });
    setOpen(true);
  };

  const save = async () => {
    if (!form.customer_name.trim() && !form.customer_id) {
      toast.error("Müşteri seçin veya isim girin");
      return;
    }
    const payload = {
      ...form,
      damage_amount: Number(form.damage_amount) || 0,
      approved_amount: Number(form.approved_amount) || 0,
      collected_amount: Number(form.collected_amount) || 0,
      vat_rate: Number(form.vat_rate) || 0,
      incident_date: form.incident_date || null,
    };
    try {
      if (editing) { await api.put(`/insurance/${editing.id}`, payload); toast.success("Güncellendi"); }
      else { await api.post("/insurance", payload); toast.success("Sigorta kaydı oluşturuldu"); }
      setOpen(false); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kaydedilemedi");
    }
  };

  const del = async () => { await api.delete(`/insurance/${deleteId}`); toast.success("Silindi"); setDeleteId(null); load(); };

  const onSelectCustomer = (id) => {
    const c = customers.find((x) => x.id === id);
    setForm({
      ...form,
      customer_id: id,
      customer_name: c?.name || form.customer_name,
      plate: c?.plate || form.plate,
      car_model: c?.car_model || form.car_model,
    });
  };

  // Ay bazlı filtreleme + geçmiş aylardan devreden bekleyen alacakları da göster
  const itemsInMonth = useMemo(() => {
    return items.filter((c) => {
      const cm = (c.date || "").slice(0, 7);
      const collectedMonth = (c.collected_date || "").slice(0, 7);
      // Bu ay açılanlar
      if (cm === selectedMonth) return true;
      // Bu ay tahsil edilen devir kayıtları
      if (c.status === "Tahsil Edildi" && collectedMonth === selectedMonth) return true;
      // Devir: geçmiş ayda açılmış ve hâlâ ödenmeyen
      const isPastMonth = cm && cm < selectedMonth;
      const stillPending = !["Tahsil Edildi", "Reddedildi"].includes(c.status);
      return isPastMonth && stillPending;
    });
  }, [items, selectedMonth]);

  const filtered = useMemo(() => {
    const source = itemsInMonth;
    if (tab === "collected") return source.filter((c) => c.status === "Tahsil Edildi");
    if (tab === "pending") return source.filter((c) => !["Tahsil Edildi", "Reddedildi"].includes(c.status));
    return source;
  }, [itemsInMonth, tab]);

  const activeMonthLabel = monthOptions.find((o) => o.value === selectedMonth)?.label;

  const uploadFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !detailCase) return;
    const fd = new FormData();
    fd.append("file", file);
    setUploading(true);
    try {
      await api.post(`/insurance/${detailCase.id}/files`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Dosya yüklendi");
      load();
    } catch (err) {
      toast.error("Yükleme başarısız");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const removeFile = async (fid) => {
    await api.delete(`/insurance/${detailCase.id}/files/${fid}`);
    toast.success("Dosya silindi");
    load();
  };

  const remaining = (c) => Math.max((c.damage_amount || 0) - (c.collected_amount || 0), 0);

  return (
    <div data-testid="insurance-page" className="space-y-6">
      {/* HEADER — month filter */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <p className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Sigorta İşleri</p>
          <p className="text-sm text-slate-700">
            Ay: <span data-testid="ins-active-month-label" className="font-semibold text-slate-900">{activeMonthLabel}</span>
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Calendar className="w-4 h-4 text-slate-500" />
          <Select value={selectedMonth} onValueChange={setSelectedMonth}>
            <SelectTrigger data-testid="ins-month-select" className="w-52">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {monthOptions.map((o) => (
                <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Summary cards — ay bazlı */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white border border-slate-200 rounded-lg p-5 shadow-sm">
          <p className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Toplam Hasar</p>
          <p className="font-display text-2xl font-bold text-slate-900 mt-2 font-mono-num" data-testid="ins-total-damage">
            {formatTRY(summary?.total_damage || 0)}
          </p>
        </div>
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-5">
          <p className="text-[11px] uppercase tracking-widest text-emerald-800 font-semibold">Tahsil Edilen</p>
          <p className="font-display text-2xl font-bold text-emerald-700 mt-2 font-mono-num" data-testid="ins-total-collected">
            {formatTRY(summary?.total_collected || 0)}
          </p>
        </div>
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-5">
          <p className="text-[11px] uppercase tracking-widest text-amber-800 font-semibold">Bekleyen Alacak</p>
          <p className="font-display text-2xl font-bold text-amber-700 mt-2 font-mono-num" data-testid="ins-total-pending">
            {formatTRY(summary?.total_pending || 0)}
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="inline-flex bg-slate-100 rounded-md p-1">
          {TABS.map((t) => (
            <button
              key={t.key}
              data-testid={`ins-tab-${t.key}`}
              onClick={() => setTab(t.key)}
              className={`px-4 py-1.5 rounded text-sm font-medium transition-colors ${
                tab === t.key ? "bg-white shadow-sm text-slate-900" : "text-slate-600 hover:text-slate-900"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
        <Button data-testid="add-insurance-btn" onClick={openNew} className="bg-slate-900 hover:bg-slate-800">
          <Plus className="w-4 h-4 mr-2" /> Yeni Sigorta İşi
        </Button>
      </div>

      <TableWrap>
        <Thead columns={["Dosya No", "Tarih", "Müşteri / Plaka", "Sigorta", "Poliçe", "Hasar", "Tahsil", "Kalan", "Durum", ""]} />
        <tbody>
          {filtered.length === 0 && <EmptyRow colSpan={10} testId="insurance-empty" message="Bu kategoride sigorta kaydı yok." />}
          {filtered.map((c) => {
            const caseMonth = (c.date || "").slice(0, 7);
            const collectedMonth = (c.collected_date || "").slice(0, 7);
            const openedInPastMonth = caseMonth && caseMonth < selectedMonth;
            const isCarriedPending =
              openedInPastMonth &&
              !["Tahsil Edildi", "Reddedildi"].includes(c.status);
            const isCarriedCollected =
              openedInPastMonth &&
              c.status === "Tahsil Edildi" &&
              collectedMonth === selectedMonth;
            const rowTint = isCarriedPending
              ? "bg-rose-50/60 hover:bg-rose-100/60"
              : isCarriedCollected
              ? "bg-emerald-50/60 hover:bg-emerald-100/60"
              : "hover:bg-slate-50/60";
            return (
            <tr
              key={c.id}
              className={`border-b border-slate-100 cursor-pointer ${rowTint}`}
              data-testid={`insurance-row-${c.id}`}
              onClick={() => setDetailCase(c)}
            >
              <td className="px-4 py-3 font-mono-num text-sm font-semibold text-slate-900">
                {c.case_number}
                {isCarriedPending && (
                  <span
                    data-testid={`ins-carried-badge-${c.id}`}
                    className="ml-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-bold border border-rose-300 bg-rose-100 text-rose-800"
                    title="Geçmiş aylardan devir"
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-rose-500 animate-pulse" />
                    DEVİR
                  </span>
                )}
                {isCarriedCollected && (
                  <span
                    data-testid={`ins-carried-collected-badge-${c.id}`}
                    className="ml-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-bold border border-emerald-300 bg-emerald-100 text-emerald-800"
                    title={`Devir kayıt · Bu ay tahsil edildi (${formatDate(c.date)} açılış)`}
                  >
                    <CheckCircle className="w-2.5 h-2.5" />
                    DEVİR · TAHSİL
                  </span>
                )}
              </td>
              <td className="px-4 py-3 text-sm text-slate-700">{formatDate(c.date)}</td>
              <td className="px-4 py-3">
                <div className="font-semibold text-slate-900">{c.customer_name}</div>
                {c.plate && <span className="text-xs font-mono-num text-slate-500">{c.plate}</span>}
              </td>
              <td className="px-4 py-3 text-sm text-slate-700">{c.insurance_company || "-"}</td>
              <td className="px-4 py-3 text-xs font-mono-num text-slate-600">{c.policy_number || "-"}</td>
              <td className="px-4 py-3 text-sm font-mono-num text-slate-700">{formatTRY(c.damage_amount)}</td>
              <td className="px-4 py-3 text-sm font-mono-num text-emerald-700">{formatTRY(c.collected_amount)}</td>
              <td className={`px-4 py-3 text-sm font-mono-num font-bold ${isCarriedPending ? "text-rose-700" : "text-amber-700"}`}>{formatTRY(remaining(c))}</td>
              <td className="px-4 py-3">
                <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium border ${STATUS_COLOR[c.status]}`}>{c.status}</span>
              </td>
              <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-end gap-1">
                  <Button data-testid={`edit-insurance-${c.id}`} variant="ghost" size="icon" onClick={() => openEdit(c)} className="h-8 w-8">
                    <Pencil className="w-4 h-4 text-slate-600" />
                  </Button>
                  <Button data-testid={`delete-insurance-${c.id}`} variant="ghost" size="icon" onClick={() => setDeleteId(c.id)} className="h-8 w-8 hover:bg-rose-50">
                    <Trash2 className="w-4 h-4 text-rose-600" />
                  </Button>
                </div>
              </td>
            </tr>
            );
          })}
        </tbody>
      </TableWrap>

      {/* Create/Edit dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="insurance-dialog" className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display">{editing ? `Sigorta İşi · ${editing.case_number}` : "Yeni Sigorta İşi"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="bg-slate-50 border border-slate-200 rounded-md p-3 space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-slate-700 text-xs uppercase tracking-widest font-semibold">
                  Müşteri Bilgileri
                </Label>
                {customers.length > 0 && (
                  <Select value={form.customer_id} onValueChange={onSelectCustomer}>
                    <SelectTrigger data-testid="ins-customer-select" className="h-8 w-48 text-xs bg-white">
                      <SelectValue placeholder="Kayıtlı müşteriden seç…" />
                    </SelectTrigger>
                    <SelectContent>
                      {customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name} {c.plate ? `· ${c.plate}` : ""}</SelectItem>)}
                    </SelectContent>
                  </Select>
                )}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Ad Soyad *</Label>
                  <Input data-testid="ins-customer-name" value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value, customer_id: "" })} className="bg-white" />
                </div>
                <div>
                  <Label className="text-xs">Plaka</Label>
                  <Input data-testid="ins-plate" value={form.plate} onChange={(e) => setForm({ ...form, plate: e.target.value, customer_id: "" })} className="bg-white font-mono-num" />
                </div>
                <div className="col-span-2">
                  <Label className="text-xs">Araç Modeli</Label>
                  <Input data-testid="ins-car-model" value={form.car_model} onChange={(e) => setForm({ ...form, car_model: e.target.value, customer_id: "" })} className="bg-white" />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label>Sigorta Şirketi</Label>
                <Input data-testid="ins-company" value={form.insurance_company} onChange={(e) => setForm({ ...form, insurance_company: e.target.value })} placeholder="Aksigorta, Allianz…" />
              </div>
              <div>
                <Label>Poliçe No</Label>
                <Input data-testid="ins-policy-number" value={form.policy_number} onChange={(e) => setForm({ ...form, policy_number: e.target.value })} className="font-mono-num" />
              </div>
              <div>
                <Label>Poliçe Tipi</Label>
                <Select value={form.policy_type} onValueChange={(v) => setForm({ ...form, policy_type: v })}>
                  <SelectTrigger data-testid="ins-policy-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {POLICY_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-4 gap-3">
              <div>
                <Label>Hasar Tutarı (₺)</Label>
                <Input data-testid="ins-damage" type="number" step="0.01" value={form.damage_amount} onChange={(e) => setForm({ ...form, damage_amount: e.target.value })} className="font-mono-num" />
              </div>
              <div>
                <Label>Tahsil Edilen (₺)</Label>
                <Input data-testid="ins-collected" type="number" step="0.01" value={form.collected_amount} onChange={(e) => setForm({ ...form, collected_amount: e.target.value })} className="font-mono-num" />
              </div>
              <div>
                <Label>KDV</Label>
                <Select value={String(form.vat_rate)} onValueChange={(v) => setForm({ ...form, vat_rate: Number(v) })}>
                  <SelectTrigger data-testid="ins-vat"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="0">KDV Yok</SelectItem>
                    <SelectItem value="0.2">%20 KDV</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Durum</Label>
                <Select
                  value={form.status}
                  onValueChange={(v) => {
                    const dmg = Number(form.damage_amount) || 0;
                    const col = Number(form.collected_amount) || 0;
                    if (v === "Tahsil Edildi" && dmg > 0 && col === 0) {
                      setForm({ ...form, status: v, collected_amount: dmg });
                    } else if (v === "Beklemede") {
                      setForm({ ...form, status: v, collected_amount: 0 });
                    } else {
                      setForm({ ...form, status: v });
                    }
                  }}
                >
                  <SelectTrigger data-testid="ins-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            {Number(form.damage_amount) > 0 && Number(form.vat_rate) > 0 && (
              <div className="bg-blue-50 border border-blue-200 rounded px-3 py-2 text-xs flex items-center justify-between">
                <span className="text-blue-700">
                  KDV Hariç: <span className="font-mono-num">{formatTRY(Number(form.damage_amount))}</span>{" "}
                  · KDV: <span className="font-mono-num">{formatTRY(Number(form.damage_amount) * Number(form.vat_rate))}</span>
                </span>
                <span className="font-bold text-blue-900 font-mono-num">
                  KDV Dahil: {formatTRY(Number(form.damage_amount) * (1 + Number(form.vat_rate)))}
                </span>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>İptal</Button>
            <Button data-testid="save-insurance-btn" onClick={save} className="bg-slate-900 hover:bg-slate-800">Kaydet</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail dialog with file uploads */}
      <Dialog open={!!detailCase} onOpenChange={(v) => !v && setDetailCase(null)}>
        <DialogContent data-testid="insurance-detail-dialog" className="sm:max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display">
              {detailCase?.case_number} · {detailCase?.customer_name}
            </DialogTitle>
          </DialogHeader>
          {detailCase && (
            <div className="space-y-5 py-2">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div><span className="text-slate-500">Plaka:</span> <span className="font-mono-num font-semibold">{detailCase.plate || "-"}</span></div>
                <div><span className="text-slate-500">Araç:</span> {detailCase.car_model || "-"}</div>
                <div><span className="text-slate-500">Şirket:</span> {detailCase.insurance_company || "-"}</div>
                <div><span className="text-slate-500">Poliçe:</span> <span className="font-mono-num">{detailCase.policy_number || "-"}</span></div>
                <div><span className="text-slate-500">Tip:</span> {detailCase.policy_type}</div>
                <div><span className="text-slate-500">Sigortalı:</span> {detailCase.insured_name || "-"}</div>
                <div><span className="text-slate-500">Hasar Tarihi:</span> {detailCase.incident_date ? formatDate(detailCase.incident_date) : "-"}</div>
                <div><span className="text-slate-500">Durum:</span> <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium border ${STATUS_COLOR[detailCase.status]}`}>{detailCase.status}</span></div>
              </div>

              <div className="grid grid-cols-4 gap-3 bg-slate-50 border border-slate-200 rounded-md p-3 text-sm">
                <div>
                  <p className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Hasar</p>
                  <p className="font-mono-num font-bold text-slate-900">{formatTRY(detailCase.damage_amount)}</p>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Tahsil</p>
                  <p className="font-mono-num font-bold text-emerald-700">{formatTRY(detailCase.collected_amount)}</p>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Kalan</p>
                  <p className="font-mono-num font-bold text-amber-700">{formatTRY(remaining(detailCase))}</p>
                </div>
              </div>

              {detailCase.notes && (
                <div>
                  <p className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold mb-1">Notlar</p>
                  <p className="text-sm text-slate-700 bg-slate-50 border border-slate-200 rounded p-3">{detailCase.notes}</p>
                </div>
              )}

              {/* Files */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <p className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Dosyalar · Resimler</p>
                  <div>
                    <input ref={fileInputRef} type="file" onChange={uploadFile} className="hidden" data-testid="ins-file-input" />
                    <Button
                      data-testid="ins-upload-btn"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploading}
                      size="sm"
                      className="bg-blue-600 hover:bg-blue-700"
                    >
                      <Upload className="w-3.5 h-3.5 mr-1" /> {uploading ? "Yükleniyor…" : "Dosya Ekle"}
                    </Button>
                  </div>
                </div>
                {detailCase.files?.length === 0 && (
                  <div className="text-center py-8 text-slate-500 text-sm bg-slate-50 border border-dashed border-slate-300 rounded">
                    Henüz dosya yüklenmedi. Poliçe, ekspertiz raporu, hasar fotoğrafları ekleyebilirsiniz.
                  </div>
                )}
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {detailCase.files?.map((f) => {
                    const isImg = f.content_type?.startsWith("image/");
                    const url = `${API}/insurance/${detailCase.id}/files/${f.id}`;
                    return (
                      <div key={f.id} data-testid={`ins-file-${f.id}`} className="relative group border border-slate-200 rounded-md overflow-hidden bg-white">
                        {isImg ? (
                          <a href={url} target="_blank" rel="noreferrer">
                            <img src={url} alt={f.filename} className="w-full h-32 object-cover" />
                          </a>
                        ) : (
                          <a href={url} target="_blank" rel="noreferrer" className="flex flex-col items-center justify-center h-32 bg-slate-50">
                            <FileText className="w-8 h-8 text-slate-500" />
                            <span className="text-[10px] text-slate-600 mt-1">Belge</span>
                          </a>
                        )}
                        <div className="p-2 border-t border-slate-100">
                          <p className="text-xs text-slate-800 truncate" title={f.filename}>{f.filename}</p>
                          <div className="flex items-center justify-between mt-1">
                            <span className="text-[10px] text-slate-500">{(f.size / 1024).toFixed(0)} KB</span>
                            <div className="flex gap-1">
                              <a href={url} target="_blank" rel="noreferrer" className="text-blue-600 hover:text-blue-800">
                                <Download className="w-3.5 h-3.5" />
                              </a>
                              <button data-testid={`ins-remove-file-${f.id}`} onClick={() => removeFile(f.id)} className="text-rose-600 hover:text-rose-800">
                                <X className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={(v) => !v && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Sigorta kaydı silinsin mi?</AlertDialogTitle>
            <AlertDialogDescription>Kayıt ve yüklenen dosya referansları silinir.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Vazgeç</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-insurance" onClick={del} className="bg-rose-600 hover:bg-rose-700">Sil</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
